import { Add as AddIcon, Sync as SyncIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import PartnerCommissionApi from "../../../infrastructure/api/partnerCommissionApi";
import { getEnumLabel } from "../SLN/market-growth/constants/commissionEnums";
import PartnerCommissionPageShell from "./components/PartnerCommissionPageShell";
import PartnerStatCard from "./components/PartnerStatCard";
import RequestPayoutDialog from "./components/RequestPayoutDialog";
import {
  PARTNER_PAYOUT_STATUS_LABELS,
  getPayoutStatusChipColor,
} from "./constants/portalEnums";
import { formatMoney } from "./utils/formatMoney";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("fa-IR") : "—";

const PartnerPayoutsPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noPartnerProfile, setNoPartnerProfile] = useState(false);
  const [summary, setSummary] = useState(null);
  const [items, setItems] = useState([]);
  const [requestOpen, setRequestOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNoPartnerProfile(false);
    try {
      const [summaryRes, payoutsRes] = await Promise.all([
        PartnerCommissionApi.getPayoutSummary(),
        PartnerCommissionApi.getPayouts({ page: 1, pageSize: 50 }),
      ]);

      const summaryNorm = PartnerCommissionApi.normalize(
        summaryRes,
        "بارگذاری خلاصه تسویه ناموفق بود."
      );
      const payoutsNorm = PartnerCommissionApi.normalize(
        payoutsRes,
        "بارگذاری تاریخچه تسویه ناموفق بود."
      );

      if (summaryNorm.noPartnerProfile || payoutsNorm.noPartnerProfile) {
        setNoPartnerProfile(true);
        setSummary(null);
        setItems([]);
        return;
      }

      const firstError = summaryNorm.error || payoutsNorm.error;
      if (firstError) {
        setError(firstError);
        setSummary(null);
        setItems([]);
        return;
      }

      setSummary(summaryNorm.data);
      const pageData = payoutsNorm.data;
      setItems(Array.isArray(pageData?.items) ? pageData.items : []);
    } catch (e) {
      setError(e?.message || "خطا در بارگذاری");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleRequestPayout = async (payload) => {
    setSaving(true);
    try {
      const res = await PartnerCommissionApi.requestPayout(payload);
      const normalized = PartnerCommissionApi.normalize(
        res,
        "ثبت درخواست تسویه ناموفق بود."
      );
      if (normalized.error) return { error: normalized.error };
      toast.success("درخواست تسویه ثبت شد.");
      setRequestOpen(false);
      await load();
      return {};
    } catch (e) {
      return { error: e?.message || "خطا در ثبت درخواست" };
    } finally {
      setSaving(false);
    }
  };

  return (
    <PartnerCommissionPageShell
      title="تسویه حساب"
      description="خلاصه مبالغ قابل برداشت و تاریخچه درخواست‌ها."
      loading={loading}
      error={error}
      noPartnerProfile={noPartnerProfile}
      onRetry={load}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setRequestOpen(true)}
          disabled={!summary?.canRequestPayout}
        >
          درخواست تسویه
        </Button>
        <Tooltip title="بارگذاری مجدد">
          <IconButton onClick={load}>
            <SyncIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {!summary?.canRequestPayout && summary?.blockingReason ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {summary.blockingReason}
        </Alert>
      ) : null}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={4}>
          <PartnerStatCard label="قابل تسویه" value={formatMoney(summary?.payableAmount)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PartnerStatCard label="در انتظار" value={formatMoney(summary?.pendingAmount)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PartnerStatCard label="پرداخت‌شده" value={formatMoney(summary?.paidAmount)} />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <PartnerStatCard
            label="آخرین پرداخت"
            value={formatDate(summary?.lastPayoutDate)}
          />
        </Grid>
        {summary?.minimumPayoutAmount != null ? (
          <Grid item xs={12} sm={6} md={4}>
            <PartnerStatCard
              label="حداقل مبلغ تسویه"
              value={formatMoney(summary.minimumPayoutAmount)}
            />
          </Grid>
        ) : null}
      </Grid>

      <Card variant="outlined">
        <CardContent sx={{ overflowX: "auto", p: 0 }}>
          <Typography variant="subtitle2" sx={{ p: 2, pb: 0 }}>
            تاریخچه درخواست‌ها
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>مبلغ</TableCell>
                <TableCell>وضعیت</TableCell>
                <TableCell>تاریخ درخواست</TableCell>
                <TableCell>تاریخ تأیید</TableCell>
                <TableCell>تاریخ پرداخت</TableCell>
                <TableCell>توضیحات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    درخواست تسویه‌ای ثبت نشده است.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((row) => (
                  <TableRow key={row.payoutId} hover>
                    <TableCell>{formatMoney(row.amount)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={getEnumLabel(PARTNER_PAYOUT_STATUS_LABELS, row.status)}
                        color={getPayoutStatusChipColor(row.status)}
                      />
                    </TableCell>
                    <TableCell>{formatDate(row.requestedAt)}</TableCell>
                    <TableCell>{formatDate(row.approvedAt)}</TableCell>
                    <TableCell>{formatDate(row.paidAt)}</TableCell>
                    <TableCell>{row.description || "—"}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <RequestPayoutDialog
        open={requestOpen}
        onClose={() => !saving && setRequestOpen(false)}
        summary={summary}
        onSubmit={handleRequestPayout}
        saving={saving}
      />
    </PartnerCommissionPageShell>
  );
};

export default PartnerPayoutsPage;
